import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import {
  setupAppPageBodyBackground,
  isMobViewport,
} from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "./hallTimer";
import {
  NEWSPAPER_LETTERS,
  NEWSPAPER_LETTER_TARGET,
  NEWSPAPER_POINTS,
  NEWSPAPER_UNLOCKS,
  VAULT_PASSWORD,
  VAULT_PASSWORD_CHARS,
  VAULT_PASSWORD_GROUPS,
  isVaultPasswordCorrect,
} from "./newspaperData";
import { unlockHall1Hotspot, getRedirectIfHall1StageDone } from "./medalDisplayData";
import deskBg from "./stage5/stage5_desk.jpg";
import mobBg from "./stage5/stage5_mob.jpg";
import blurDesk from "../img/blur_Desk.jpg";
import blurMob from "../img/blur_mob.jpg";
import trophyDesk from "./stage5/trophy_desk.jpg";
import trophyMob from "./stage5/trophy_mob.jpg";
import continueBtn from "./stage5/continue.png";
import "../fonts/breuer-headline.css";
import "./stage5.css";

/** Large Illustrator SVGs — served from /public (embedded binary). */
const NEWSPAPER_DESK_SVG = `${process.env.PUBLIC_URL || ""}/hall_1/stage5/newspapaper_desk.svg`;
const NEWSPAPER_MOB_SVG = `${process.env.PUBLIC_URL || ""}/hall_1/stage5/newspapaper_mob.svg`;

const LETTER_IDS = NEWSPAPER_LETTERS.map((l) => l.id);
const LETTER_BY_ID = Object.fromEntries(
  NEWSPAPER_LETTERS.map((l) => [l.id, l])
);

function useNewspaperSvgUrl(deskUrl, mobUrl) {
  const [svgUrl, setSvgUrl] = useState(() =>
    typeof window !== "undefined" && isMobViewport() ? mobUrl : deskUrl
  );

  useEffect(() => {
    const sync = () => {
      const next = isMobViewport() ? mobUrl : deskUrl;
      setSvgUrl((prev) => (prev === next ? prev : next));
    };
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, [deskUrl, mobUrl]);

  return svgUrl;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function styleLetter(el, { found }) {
  if (!el) return;
  el.style.pointerEvents = "auto";
  el.style.cursor = "pointer";
  el.style.outline = "none";
  el.style.filter = found
    ? "drop-shadow(0 0 3px #fff6c8) brightness(1.2)"
    : "none";
  el.style.opacity = found ? "1" : "0.92";
  el.setAttribute("role", "button");
  el.setAttribute("tabindex", "0");
  el.setAttribute("aria-pressed", found ? "true" : "false");
}

/** Hall Stage 5 — Curator’s Newspaper Investigation */
export default function HallStage5() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  // inspect → password → saving → success
  const [phase, setPhase] = useState("inspect");
  /** Letter ids in the order the player clicked them. */
  const [selectedIds, setSelectedIds] = useState(() => []);
  const [slots, setSlots] = useState(() =>
    VAULT_PASSWORD_CHARS.map(() => "")
  );
  const [activeSlot, setActiveSlot] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [shake, setShake] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [svgReady, setSvgReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const completingRef = useRef(false);
  const slotRefs = useRef([]);
  const objectRef = useRef(null);
  const selectedIdsRef = useRef(selectedIds);
  const toggleLetterRef = useRef(null);
  const newspaperSvgUrl = useNewspaperSvgUrl(NEWSPAPER_DESK_SVG, NEWSPAPER_MOB_SVG);

  const collectedCount = useMemo(
    () =>
      selectedIds.reduce(
        (sum, id) => sum + (LETTER_BY_ID[id]?.count || 1),
        0
      ),
    [selectedIds]
  );
  const allFound = collectedCount >= NEWSPAPER_LETTER_TARGET;

  const liveStagePoints = useMemo(
    () =>
      liveProgressPoints(
        collectedCount,
        NEWSPAPER_LETTER_TARGET,
        NEWSPAPER_POINTS
      ),
    [collectedCount]
  );

  const displayPoints =
    phase === "success" ? totalScore : totalScore + liveStagePoints;

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-1"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall1-stage5-page");
    // Inspect = blur fill behind SVG; password = desk photo; success = trophy.
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground:
        phase === "inspect"
          ? blurDesk
          : phase === "success"
            ? trophyDesk
            : deskBg,
      mobForeground:
        phase === "inspect"
          ? blurMob
          : phase === "success"
            ? trophyMob
            : mobBg,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall1-stage5-page");
    };
  }, [phase]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall1StageDone(stage, 5);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || phase === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, phase]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (phase === "inspect" || phase === "password") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    phase,
  ]);

  useEffect(() => {
    if (phase !== "password") return;
    const t = window.setTimeout(() => {
      slotRefs.current[0]?.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [phase]);

  const typedPassword = useMemo(() => slots.join(""), [slots]);
  const passwordCorrect = useMemo(
    () => isVaultPasswordCorrect(typedPassword),
    [typedPassword]
  );
  const passwordLocked = passwordCorrect || phase === "saving";

  const collectedLetters = useMemo(() => {
    const letters = [];
    selectedIds.forEach((id) => {
      const entry = LETTER_BY_ID[id];
      if (!entry) return;
      const times = entry.count || 1;
      for (let i = 0; i < times; i += 1) {
        letters.push({ id, letter: entry.letter, key: `${id}-${i}` });
      }
    });
    return letters;
  }, [selectedIds]);

  const toggleLetter = useCallback(
    (id) => {
      if (phase !== "inspect") return;
      if (!LETTER_BY_ID[id]) return;
      setSelectedIds((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        return [...prev, id];
      });
      setErrorMsg("");
    },
    [phase]
  );

  useEffect(() => {
    toggleLetterRef.current = toggleLetter;
  }, [toggleLetter]);

  const bindLetters = useCallback(() => {
    const obj = objectRef.current;
    if (!obj || phase !== "inspect") return;
    let doc = null;
    try {
      doc = obj.contentDocument;
    } catch {
      doc = null;
    }
    if (!doc) return;

    // Prevent an inner scrollbar inside the <object> document (esp. mobile portrait)
    if (doc.documentElement) {
      doc.documentElement.style.overflow = "hidden";
      doc.documentElement.style.width = "100%";
      doc.documentElement.style.height = "100%";
    }
    if (doc.body) {
      doc.body.style.overflow = "hidden";
      doc.body.style.margin = "0";
      doc.body.style.width = "100%";
      doc.body.style.height = "100%";
    }

    const svg = doc.querySelector("svg");
    if (svg) {
      svg.style.width = "100%";
      svg.style.height = "100%";
      svg.style.maxWidth = "100%";
      svg.style.maxHeight = "100%";
      svg.style.display = "block";
      svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    }

    // Keep desk art from stealing clicks meant for letters
    const bg = doc.getElementById("bg");
    if (bg) {
      bg.style.pointerEvents = "none";
    }
    // Mob SVG embeds the desk photo as an unlabeled <image>
    doc.querySelectorAll("image").forEach((img) => {
      const id = img.getAttribute("id") || "";
      if (!LETTER_IDS.includes(id)) {
        img.style.pointerEvents = "none";
      }
    });

    // A2 removed from puzzle — only the single A hotspot counts (as 2 letters)
    const orphanA2 = doc.getElementById("A2");
    if (orphanA2) {
      orphanA2.style.pointerEvents = "none";
      orphanA2.style.cursor = "default";
      orphanA2.removeAttribute("role");
      orphanA2.removeAttribute("tabindex");
    }

    LETTER_IDS.forEach((id) => {
      let el = doc.getElementById(id);
      if (!el) {
        console.warn(`[Stage5] missing letter id: ${id}`);
        return;
      }

      const fresh = el.cloneNode(true);
      el.parentNode?.replaceChild(fresh, el);
      el = fresh;

      const found = selectedIdsRef.current.includes(id);
      styleLetter(el, { found });
      el.setAttribute(
        "aria-label",
        `Letter ${LETTER_BY_ID[id]?.letter || id}${found ? ", selected" : ""}`
      );

      const onActivate = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleLetterRef.current?.(id);
      };

      el.addEventListener("click", onActivate);
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") onActivate(e);
      });
    });

    setSvgReady(true);
  }, [phase]);

  // Re-style found letters without rebinding listeners every time
  useEffect(() => {
    if (phase !== "inspect" || !svgReady) return;
    const obj = objectRef.current;
    const doc = obj?.contentDocument;
    if (!doc) return;
    LETTER_IDS.forEach((id) => {
      const el = doc.getElementById(id);
      if (!el) return;
      const found = selectedIds.includes(id);
      styleLetter(el, { found });
      el.setAttribute(
        "aria-label",
        `Letter ${LETTER_BY_ID[id]?.letter || id}${found ? ", selected" : ""}`
      );
    });
  }, [selectedIds, phase, svgReady]);

  useEffect(() => {
    if (phase !== "inspect") {
      setSvgReady(false);
      return undefined;
    }

    setSvgReady(false);
    const obj = objectRef.current;
    if (!obj) return undefined;

    if (obj.contentDocument?.querySelector("svg")) {
      bindLetters();
    }

    obj.addEventListener("load", bindLetters);
    return () => {
      obj.removeEventListener("load", bindLetters);
    };
  }, [bindLetters, phase, newspaperSvgUrl]);

  const goToPassword = useCallback(() => {
    if (!allFound) {
      setErrorMsg("Collect every highlighted letter first.");
      setShake(true);
      window.setTimeout(() => setShake(false), 420);
      return;
    }
    setErrorMsg("");
    setSlots(VAULT_PASSWORD_CHARS.map(() => ""));
    setActiveSlot(0);
    setPhase("password");
  }, [allFound]);

  const completePuzzle = useCallback(async () => {
    if (completingRef.current || phase === "success" || phase === "saving") {
      return;
    }
    completingRef.current = true;
    setPhase("saving");
    setErrorMsg("");

    const timeStr = formatSecondsToClock(elapsedRef.current);
    try {
      let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId && !isDemoBypass && backendBase && adminToken) {
        stageId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
        });
        if (stageId) setStageRowId(String(stageId));
      }

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const result = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 5,
          score: NEWSPAPER_POINTS,
          answers: {
            puzzle: "newspaper_investigation",
            password: VAULT_PASSWORD,
            found: [...selectedIds],
          },
          time: timeStr,
        });
        if (result?.total_score != null) {
          setTotalScore(Number(result.total_score) || 0);
        } else {
          setTotalScore((prev) => prev + NEWSPAPER_POINTS);
        }
      } else {
        setTotalScore((prev) => prev + NEWSPAPER_POINTS);
      }

      unlockHall1Hotspot(NEWSPAPER_UNLOCKS);
      setPhase("success");
    } catch (err) {
      console.error("Newspaper investigation save:", err);
      completingRef.current = false;
      setPhase("password");
      setErrorMsg("Could not save progress. Please try again.");
    }
  }, [
    phase,
    stageRowId,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
    selectedIds,
  ]);

  const handleUnlock = useCallback(() => {
    if (phase !== "password") return;
    if (slots.some((c) => !c)) {
      setErrorMsg("Fill every letter of the password.");
      setShake(true);
      window.setTimeout(() => setShake(false), 420);
      return;
    }
    if (!isVaultPasswordCorrect(typedPassword)) {
      setErrorMsg("Incorrect code. Check the letters you collected.");
      setShake(true);
      window.setTimeout(() => setShake(false), 420);
      return;
    }
    completePuzzle();
  }, [phase, slots, typedPassword, completePuzzle]);

  const setSlotChar = useCallback((index, raw) => {
    if (passwordLocked) return;
    const ch = String(raw || "")
      .toUpperCase()
      .replace(/[^A-Z]/g, "")
      .slice(-1);
    setSlots((prev) => {
      const next = [...prev];
      next[index] = ch;
      return next;
    });
    setErrorMsg("");
    if (ch && index < VAULT_PASSWORD_CHARS.length - 1) {
      const nextIdx = index + 1;
      setActiveSlot(nextIdx);
      requestAnimationFrame(() => slotRefs.current[nextIdx]?.focus());
    }
  }, [passwordLocked]);

  const fillSlotFromCollected = useCallback(
    (letter) => {
      if (passwordLocked || phase === "saving") return;
      const target =
        activeSlot >= 0 && activeSlot < VAULT_PASSWORD_CHARS.length
          ? activeSlot
          : slots.findIndex((ch) => !ch);
      if (target < 0) return;
      setSlotChar(target, letter);
    },
    [passwordLocked, phase, activeSlot, slots, setSlotChar]
  );

  // Build password slot groups with visual gaps
  const slotGroups = useMemo(() => {
    const groups = [];
    let idx = 0;
    VAULT_PASSWORD_GROUPS.forEach((size) => {
      groups.push(
        Array.from({ length: size }, (_, i) => ({
          index: idx + i,
        }))
      );
      idx += size;
    });
    return groups;
  }, []);

  return (
    <div
      className={`hall1-stage5 newspaper-invest newspaper-invest--${phase}`}
    >
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      {/* —— Phase 1: inspect (ref 9.jpg) —— */}
      {phase === "inspect" ? (
        <div className="newspaper-invest__scene">
          <header className="newspaper-invest__header newspaper-invest__header--overlay">
            <h1 className="newspaper-invest__title">
            NEWSPAPER INVESTIGATION
            </h1>
          </header>

          <div className="newspaper-invest__svg-scroller">
            <object
              key={newspaperSvgUrl}
              ref={objectRef}
              className="newspaper-invest__svg"
              data={newspaperSvgUrl}
              type="image/svg+xml"
              aria-label="Newspaper on curator desk"
            >
              <p className="newspaper-invest__svg-fallback">
                Unable to load newspaper scene.
              </p>
            </object>
          </div>

          {!svgReady ? (
            <p className="newspaper-invest__loading" aria-live="polite">
              Loading newspaper…
            </p>
          ) : null}

          <div className="newspaper-invest__hud">
            <div className="newspaper-invest__copy">
              <div className="newspaper-invest__rule" aria-hidden>
                <span />
              </div>
              <p className="newspaper-invest__instruction">
                Search the newspaper carefully and click the highlighted
                letters to reveal the Trophy Vault password.
              </p>
              <div className="newspaper-invest__rule" aria-hidden>
                <span />
              </div>
            </div>

            <div className="newspaper-invest__dock">
              <div className="newspaper-invest__found">
                <div
                  className="newspaper-invest__found-letters"
                  aria-label="Collected letters"
                >
                  {collectedLetters.length === 0 ? (
                    <span className="newspaper-invest__found-empty">—</span>
                  ) : (
                    collectedLetters.map(({ id, letter, key }) => (
                      <button
                        key={key}
                        type="button"
                        className="newspaper-invest__letter-chip"
                        onClick={() => toggleLetter(id)}
                        title={`Remove letter ${letter}`}
                        aria-label={`Collected letter ${letter}. Click to remove.`}
                      >
                        {letter}
                      </button>
                    ))
                  )}
                </div>
                <p className="newspaper-invest__found-count">
                  {collectedCount}/{NEWSPAPER_LETTER_TARGET} letters found
                </p>
              </div>

              {errorMsg ? (
                <p className="newspaper-invest__error" role="alert">
                  {errorMsg}
                </p>
              ) : null}

              {allFound ? (
                <button
                  type="button"
                  className={`newspaper-invest__cta${
                    shake ? " newspaper-invest__cta--shake" : ""
                  }`}
                  style={{ backgroundImage: `url(${continueBtn})` }}
                  onClick={goToPassword}
                >
                  FORM PASSWORD
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* —— Phase 2: password entry (ref 10.jpg) —— */}
      {phase === "password" || phase === "saving" ? (
        <div className="newspaper-password">
          <header className="newspaper-invest__header">
            <h1 className="newspaper-invest__title">
              PASSWORD ENTRY
            </h1>
          </header>

          <div className="newspaper-password__panel">
            <h2 className="newspaper-password__heading">PASSWORD</h2>
            <div className="newspaper-password__rule" aria-hidden>
              <span />
            </div>

            <div className="newspaper-password__collected">
              <p className="newspaper-password__collected-label">
                Letters you collected
              </p>
              <div
                className="newspaper-password__collected-letters"
                aria-label="Tap a letter to fill the password"
              >
                {collectedLetters.map(({ letter, key }) => (
                  <button
                    key={key}
                    type="button"
                    className="newspaper-invest__letter-chip newspaper-invest__letter-chip--fill"
                    disabled={passwordLocked || phase === "saving"}
                    onClick={() => fillSlotFromCollected(letter)}
                    title={`Use letter ${letter}`}
                    aria-label={`Use collected letter ${letter}`}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            </div>

            <div
              className={`newspaper-password__slots${
                shake ? " newspaper-password__slots--shake" : ""
              }`}
              role="group"
              aria-label="Vault password letters"
            >
              {slotGroups.map((group, gi) => (
                <div key={gi} className="newspaper-password__group">
                  {group.map(({ index }) => (
                    <input
                      key={index}
                      ref={(el) => {
                        slotRefs.current[index] = el;
                      }}
                      className={`newspaper-password__box${
                        activeSlot === index
                          ? " newspaper-password__box--active"
                          : ""
                      }${
                        passwordCorrect
                          ? " newspaper-password__box--locked"
                          : ""
                      }`}
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      maxLength={1}
                      value={slots[index]}
                      readOnly={passwordLocked}
                      disabled={phase === "saving"}
                      aria-label={`Letter ${index + 1}`}
                      aria-readonly={passwordLocked || undefined}
                      onFocus={() => setActiveSlot(index)}
                      onChange={(e) => setSlotChar(index, e.target.value)}
                      onCut={(e) => {
                        if (passwordLocked) e.preventDefault();
                      }}
                      onPaste={(e) => {
                        if (passwordLocked) e.preventDefault();
                      }}
                      onKeyDown={(e) => {
                        if (passwordLocked) {
                          if (e.key === "Enter") handleUnlock();
                          else if (
                            e.key === "Backspace" ||
                            e.key === "Delete" ||
                            e.key.length === 1
                          ) {
                            e.preventDefault();
                          }
                          return;
                        }
                        if (e.key === "Backspace" && !slots[index] && index > 0) {
                          e.preventDefault();
                          setActiveSlot(index - 1);
                          slotRefs.current[index - 1]?.focus();
                          setSlotChar(index - 1, "");
                        }
                        if (e.key === "ArrowLeft" && index > 0) {
                          e.preventDefault();
                          setActiveSlot(index - 1);
                          slotRefs.current[index - 1]?.focus();
                        }
                        if (
                          e.key === "ArrowRight" &&
                          index < VAULT_PASSWORD_CHARS.length - 1
                        ) {
                          e.preventDefault();
                          setActiveSlot(index + 1);
                          slotRefs.current[index + 1]?.focus();
                        }
                        if (e.key === "Enter") handleUnlock();
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>

            {errorMsg ? (
              <p className="newspaper-invest__error" role="alert">
                {errorMsg}
              </p>
            ) : null}

            <button
              type="button"
              className="newspaper-invest__cta newspaper-invest__cta--password"
              style={{ backgroundImage: `url(${continueBtn})` }}
              disabled={phase === "saving"}
              onClick={handleUnlock}
            >
              {phase === "saving" ? "SAVING…" : "UNLOCK VAULT"}
            </button>

            <button
              type="button"
              className="newspaper-password__back"
              disabled={passwordLocked}
              onClick={() => {
                if (passwordLocked) return;
                setPhase("inspect");
                setErrorMsg("");
              }}
            >
              ‹ Back to newspaper
            </button>
          </div>
        </div>
      ) : null}

      {/* —— Phase 3: success (ref 11.jpg) —— */}
      {phase === "success" ? (
        <div className="newspaper-vault-success" role="dialog" aria-modal="true">
          {/* <header className="newspaper-invest__header">
            <h1 className="newspaper-invest__title">SUCCESS</h1>
          </header> */}

          <div className="newspaper-vault-success__content">
            {/* <h2 className="newspaper-vault-success__heading">Password found!</h2>
            <p className="newspaper-vault-success__text">
              Time to unlock the Trophy Vault.
            </p> */}
            <button
              type="button"
              className="newspaper-invest__cta newspaper-invest__cta--wide"
              style={{ backgroundImage: `url(${continueBtn})` }}
              onClick={() => navigate("/hall-1/stage6")}
            >
              CONTINUE
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
